import { createApi } from "@reduxjs/toolkit/query/react"
import { FeatureAssignmentThingTypeMrn } from "shared/featureAssignmentData"
import { FeatureThingTypeMrn } from "shared/featureData"
import { baseQuery } from "../baseQuerySl2"
import type { TemperatureDemoScenario } from "shared/locker"

type UpdateThingRequest = {
  body: Record<string, any>
  params?: Record<string, any>
}

type CreateThingRequest = {
  body: Record<string, any>
  params?: Record<string, any>
}
type CreateThingsResponse = {
  things: any[]
  ids: string[]
}

type DeleteThingRequest = {
  body: Record<string, any>
  params?: Record<string, any>
}

type GetThingsResponse = {
  things: any[]
  meta: {
    count: number
    nextToken: string | undefined
  }
}

export const thingsApi = createApi({
  reducerPath: "thingsApi",
  baseQuery,
  tagTypes: [FeatureThingTypeMrn, FeatureAssignmentThingTypeMrn],
  endpoints: (builder) => ({
    getThings: builder.query<
      GetThingsResponse,
      { params: Record<string, any> }
    >({
      query: ({ params }) => ({
        url: "things",
        params,
      }),
    }),
    getFeatures: builder.query<
      GetThingsResponse,
      { params: { tenantId: string } & Record<string, any> }
    >({
      query: ({ params }) => ({
        url: "things",
        params: {
          ...params,
          tenantId: params.tenantId,
          "ap.tenantId": params.tenantId,
          "ap.name": "GetFeatures",
        },
      }),
      providesTags: (result) =>
        result?.things
          ? [
              ...result.things.map((f: any) => ({
                type: f.thingTypeMrn || FeatureThingTypeMrn,
                id: f.id,
              })),
              { type: FeatureThingTypeMrn, id: "LIST" },
            ]
          : [{ type: FeatureThingTypeMrn, id: "LIST" }],
    }),
    getFeaturesAssignement: builder.query<
      GetThingsResponse,
      { params: { tenantId: string } & Record<string, any> }
    >({
      query: ({ params }) => ({
        url: "things",
        params: {
          ...params,
          tenantId: params.tenantId,
          "ap.tenantId": params.tenantId,
          "ap.name": "GetFeatureAssignmentsByTenantId",
        },
      }),
      providesTags: (result) =>
        result?.things
          ? [
              ...result.things.map((a: any) => ({
                type: a.thingTypeMrn || FeatureAssignmentThingTypeMrn,
                id: a.id,
              })),
              { type: FeatureAssignmentThingTypeMrn, id: "LIST" },
            ]
          : [{ type: FeatureAssignmentThingTypeMrn, id: "LIST" }],
    }),
    updateThings: builder.mutation<any, UpdateThingRequest>({
      query: ({ params = {}, body }) => ({
        url: "things",
        method: "PATCH",
        params,
        body,
      }),
    }),
    createThings: builder.mutation<CreateThingsResponse, CreateThingRequest>({
      query: ({ params = {}, body }) => ({
        url: "things",
        method: "POST",
        params,
        body,
      }),
    }),
    deleteThings: builder.mutation<any, DeleteThingRequest>({
      query: ({ params = {}, body }) => ({
        url: "things",
        method: "DELETE",
        params,
        body,
      }),
    }),
    /**
     * LOCKER-534 — replace the demo door → scenario map of one locker (full
     * replacement: an omitted door leaves the demo). Returns the updated
     * locker thing; `thingsSlice` swaps its `temperatureDemoDoors` wholesale.
     */
    putTemperatureDemoDoors: builder.mutation<
      any,
      {
        lockerId: string
        tenantId: string
        doors: Record<string, TemperatureDemoScenario>
      }
    >({
      query: ({ lockerId, tenantId, doors }) => ({
        url: `lockers/${encodeURIComponent(lockerId)}/temperature-demo-doors`,
        method: "PUT",
        params: { tenantId },
        body: { doors },
      }),
    }),
    getCurrentSmsDeliveries: builder.query<
      GetThingsResponse,
      { params: { tenantId: string } & Record<string, any> }
    >({
      query: ({ params }) => ({
        url: "things",
        params: {
          ...params,
          tenantId: params.tenantId,
          "ap.tenantId": params.tenantId,
          "ap.name": "GetCurrentSmsDeliveries",
        },
      }),
    }),
    getSmsDeliveriesByReservationId: builder.query<
      GetThingsResponse,
      {
        params: {
          tenantId: string
          reservationId: string
          limit?: number
        } & Record<string, any>
      }
    >({
      query: ({ params }) => ({
        url: "things",
        params: {
          ...params,
          tenantId: params.tenantId,
          "ap.tenantId": params.tenantId,
          "ap.name": "GetSmsDeliveriesByReservationId",
          "ap.reservationId": params.reservationId,
          ...(params.limit && { "ap.limit": params.limit }),
        },
      }),
    }),
  }),
})

export const {
  useGetThingsQuery,
  useLazyGetThingsQuery,
  useGetFeaturesQuery,
  useGetFeaturesAssignementQuery,
  useGetCurrentSmsDeliveriesQuery,
  useGetSmsDeliveriesByReservationIdQuery,
  useUpdateThingsMutation,
  useCreateThingsMutation,
  useDeleteThingsMutation,
  usePutTemperatureDemoDoorsMutation,
} = thingsApi
